import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  Shield,
  Building2,
  Users,
  Monitor,
  ClipboardCheck,
  Check,
  ChevronRight,
  ChevronLeft,
  Copy,
  Download,
  Plus,
  X,
  Loader2,
  Rocket,
  Globe,
  ArrowRight,
  Trash2,
  CheckCircle,
  Circle,
} from "lucide-react";
import { toast } from "sonner";
import {
  getOnboardingStatus,
  setupCompanyProfile,
  setupTeam,
  setupCompliance,
  completeOnboarding,
  getAvailableFrameworks,
  createAgentToken,
  getAgentTokens,
} from "../lib/api";

const STEPS = [
  {
    id: "company_profile",
    title: "Company Profile",
    description: "Tell us about your organization",
    icon: Building2,
  },
  {
    id: "team",
    title: "Team Setup",
    description: "Invite your team members",
    icon: Users,
  },
  {
    id: "agent_deployment",
    title: "Deploy Agent",
    description: "Install monitoring on devices",
    icon: Monitor,
  },
  {
    id: "compliance",
    title: "Compliance",
    description: "Select compliance frameworks",
    icon: ClipboardCheck,
  },
  {
    id: "complete",
    title: "Launch",
    description: "Review and go live",
    icon: Rocket,
  },
];

const INDUSTRIES = [
  "Managed Service Provider",
  "Healthcare",
  "Financial Services",
  "Legal",
  "Education",
  "Government",
  "Manufacturing",
  "Retail",
  "Technology",
  "Non-Profit",
  "Other",
];

const COMPANY_SIZES = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "500+", label: "500+ employees" },
];

const ROLES = [
  { value: "admin", label: "Administrator" },
  { value: "technician", label: "Technician" },
  { value: "viewer", label: "Viewer" },
];

export const SetupWizardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stepStatus, setStepStatus] = useState({});

  // Company profile state
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [website, setWebsite] = useState("");

  // Team state
  const [invites, setInvites] = useState([{ email: "", role: "technician" }]);

  // Agent state
  const [agentTokens, setAgentTokens] = useState([]);
  const [newTokenName, setNewTokenName] = useState("");
  const [creatingToken, setCreatingToken] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  // Compliance state
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);

  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    try {
      const status = await getOnboardingStatus();
      if (status.completed) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setStepStatus(status.steps || {});
      if (status.company_name) setCompanyName(status.company_name);

      // Load supplementary data
      const [frameworksRes, tokensRes] = await Promise.all([
        getAvailableFrameworks(),
        getAgentTokens().catch(() => ({ data: [] })),
      ]);
      setAvailableFrameworks(frameworksRes.frameworks || []);
      const tokens = tokensRes.data || tokensRes || [];
      setAgentTokens(Array.isArray(tokens) ? tokens.filter((t) => t.is_active) : []);
    } catch (err) {
      console.error("Failed to load onboarding status:", err);
    } finally {
      setLoading(false);
    }
  };

  // ==================== STEP HANDLERS ====================

  const handleSaveCompanyProfile = async () => {
    if (!companyName.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      await setupCompanyProfile({
        company_name: companyName.trim(),
        industry: industry || null,
        company_size: companySize || null,
        website: website || null,
      });
      setStepStatus((s) => ({ ...s, company_profile: true }));
      toast.success("Company profile saved");
      goNext();
    } catch (err) {
      toast.error("Failed to save company profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTeam = async () => {
    const validInvites = invites.filter((i) => i.email.trim());
    setSaving(true);
    try {
      if (validInvites.length > 0) {
        await setupTeam({ invitations: validInvites });
        setStepStatus((s) => ({ ...s, team: true }));
        toast.success(`Invited ${validInvites.length} team member(s)`);
      }
      goNext();
    } catch (err) {
      toast.error("Failed to send invitations");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateToken = async () => {
    const name = newTokenName.trim() || `Agent Token ${agentTokens.length + 1}`;
    setCreatingToken(true);
    try {
      const res = await createAgentToken({ name });
      const token = res.data || res;
      setAgentTokens((prev) => [...prev, token]);
      setNewTokenName("");
      setStepStatus((s) => ({ ...s, agent_deployment: true }));
      toast.success("Agent token created");
    } catch (err) {
      toast.error("Failed to create token");
    } finally {
      setCreatingToken(false);
    }
  };

  const handleSaveCompliance = async () => {
    if (selectedFrameworks.length === 0) {
      goNext();
      return;
    }
    setSaving(true);
    try {
      await setupCompliance({ frameworks: selectedFrameworks });
      setStepStatus((s) => ({ ...s, compliance: true }));
      toast.success("Compliance frameworks configured");
      goNext();
    } catch (err) {
      toast.error("Failed to configure compliance");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await completeOnboarding();
      toast.success("Setup complete! Welcome to SecureSight.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Failed to complete setup");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const goNext = () => setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const addInviteRow = () => setInvites((prev) => [...prev, { email: "", role: "technician" }]);
  const removeInviteRow = (idx) => setInvites((prev) => prev.filter((_, i) => i !== idx));
  const updateInvite = (idx, field, value) =>
    setInvites((prev) => prev.map((inv, i) => (i === idx ? { ...inv, [field]: value } : inv)));

  const toggleFramework = (name) => {
    setSelectedFrameworks((prev) =>
      prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]
    );
  };

  const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // ==================== LOADING ====================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // ==================== STEP CONTENT ====================

  const renderStepContent = () => {
    switch (currentStep) {
      // ---- COMPANY PROFILE ----
      case 0:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name *</Label>
              <Input
                id="company-name"
                placeholder="Acme MSP"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger id="industry">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-size">Company Size</Label>
              <Select value={companySize} onValueChange={setCompanySize}>
                <SelectTrigger id="company-size">
                  <SelectValue placeholder="How many employees?" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_SIZES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="website"
                  className="pl-10"
                  placeholder="https://yourcompany.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveCompanyProfile} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ---- TEAM ----
      case 1:
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Invite team members to your SecureSight workspace. They'll receive an email
              with instructions to join. You can skip this and invite people later from Settings.
            </p>
            <div className="space-y-3">
              {invites.map((inv, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <Input
                    placeholder="colleague@company.com"
                    value={inv.email}
                    onChange={(e) => updateInvite(idx, "email", e.target.value)}
                    className="flex-1"
                  />
                  <Select value={inv.role} onValueChange={(v) => updateInvite(idx, "role", v)}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {invites.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeInviteRow(idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addInviteRow}>
                <Plus className="w-4 h-4 mr-1" /> Add another
              </Button>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={goBack}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={goNext}>Skip for now</Button>
                <Button onClick={handleSaveTeam} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Send Invites
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        );

      // ---- AGENT DEPLOYMENT ----
      case 2:
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Deploy the SecureSight agent on your endpoints to start collecting health data,
              monitoring threats, and managing devices remotely.
            </p>

            {/* Step 1: Create token */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Step 1: Create an Agent Token</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Token name (e.g. Office Devices)"
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleCreateToken} disabled={creatingToken}>
                    {creatingToken ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-1" /> Create
                      </>
                    )}
                  </Button>
                </div>

                {agentTokens.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {agentTokens.map((token) => (
                      <div key={token.id} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{token.name}</p>
                          {token.token && (
                            <code className="text-xs text-muted-foreground break-all">
                              {token.token.slice(0, 32)}...
                            </code>
                          )}
                        </div>
                        {token.token && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(token.token, token.id)}
                          >
                            {copiedToken === token.id ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 2: Download & install */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Step 2: Download & Install</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <a
                    href="https://github.com/bencarpinello/securesight/releases/latest"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition"
                  >
                    <Download className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-sm">Windows</p>
                      <p className="text-xs text-muted-foreground">.exe installer</p>
                    </div>
                  </a>
                  <a
                    href="https://github.com/bencarpinello/securesight/releases/latest"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition"
                  >
                    <Download className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-sm">macOS</p>
                      <p className="text-xs text-muted-foreground">Universal binary</p>
                    </div>
                  </a>
                  <a
                    href="https://github.com/bencarpinello/securesight/releases/latest"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition"
                  >
                    <Download className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-sm">Linux</p>
                      <p className="text-xs text-muted-foreground">.deb package</p>
                    </div>
                  </a>
                </div>

                <Separator />

                <div>
                  <p className="text-sm font-medium mb-2">Step 3: Run the installer with your token</p>
                  <div className="space-y-2">
                    <div className="p-3 bg-slate-950 text-slate-200 rounded-lg font-mono text-xs">
                      <p className="text-slate-500 mb-1"># Windows (run as Administrator)</p>
                      <p>securesight-agent.exe install --agent-token YOUR_TOKEN --agent-api {apiUrl}</p>
                    </div>
                    <div className="p-3 bg-slate-950 text-slate-200 rounded-lg font-mono text-xs">
                      <p className="text-slate-500 mb-1"># macOS / Linux</p>
                      <p>securesight-agent install --agent-token YOUR_TOKEN --agent-api {apiUrl}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={goBack}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={goNext}>Skip for now</Button>
                <Button onClick={goNext} disabled={agentTokens.length === 0}>
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        );

      // ---- COMPLIANCE ----
      case 3:
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Select the compliance frameworks relevant to your business. SecureSight will
              automatically create requirements and track your compliance posture.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableFrameworks.map((fw) => {
                const selected = selectedFrameworks.includes(fw.name);
                return (
                  <div
                    key={fw.id}
                    onClick={() => toggleFramework(fw.name)}
                    className={`
                      cursor-pointer p-4 border rounded-lg transition-all
                      ${selected
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "hover:bg-muted/50"
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{fw.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {fw.item_count} requirements
                        </p>
                      </div>
                      {selected ? (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground/40" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={goBack}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={goNext}>Skip for now</Button>
                <Button onClick={handleSaveCompliance} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {selectedFrameworks.length > 0
                    ? `Add ${selectedFrameworks.length} Framework${selectedFrameworks.length > 1 ? "s" : ""}`
                    : "Continue"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        );

      // ---- COMPLETE ----
      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">You're all set!</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Your SecureSight workspace is configured. Here's a summary of what's been set up.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {STEPS.slice(0, 4).map((step) => {
                const done = stepStatus[step.id];
                return (
                  <div
                    key={step.id}
                    className={`
                      flex items-center gap-3 p-4 border rounded-lg
                      ${done ? "bg-green-50 border-green-200" : "bg-muted/30 border-dashed"}
                    `}
                  >
                    {done ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <Circle className="w-5 h-5 text-muted-foreground/40 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{step.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {done ? "Completed" : "Skipped - you can set this up later in Settings"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={goBack}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button size="lg" onClick={handleComplete} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="setup-wizard-page">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight">SecureSight</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Welcome, {user?.name}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">
            Set Up Your Workspace
          </h1>
          <p className="text-muted-foreground">
            Let's get SecureSight configured for your organization. This takes about 5 minutes.
          </p>
        </div>

        {/* Step progress */}
        <div className="flex items-center justify-between mb-8 px-4">
          {STEPS.map((step, idx) => {
            const StepIcon = step.icon;
            const isActive = idx === currentStep;
            const isDone = idx < currentStep || stepStatus[step.id];
            return (
              <React.Fragment key={step.id}>
                {idx > 0 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 rounded ${
                      idx <= currentStep ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
                <button
                  onClick={() => setCurrentStep(idx)}
                  className={`
                    flex flex-col items-center gap-1 transition-colors
                    ${isActive ? "text-primary" : isDone ? "text-green-600" : "text-muted-foreground"}
                  `}
                >
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all
                      ${isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : isDone
                          ? "bg-green-100 text-green-600"
                          : "bg-muted text-muted-foreground"
                      }
                    `}
                  >
                    {isDone && !isActive ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
            <CardDescription>{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        {/* Skip all */}
        {currentStep < STEPS.length - 1 && (
          <div className="text-center mt-6">
            <Button
              variant="link"
              className="text-muted-foreground"
              onClick={() => setCurrentStep(STEPS.length - 1)}
            >
              Skip setup and go to dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
